import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Stack } from "@mui/material";
import IconLoader from "@/features/Common/components/IconLoader";
import ScreenWrapper from "../../Common/ScreenWrapper";
import MentalWellbeingInfoCard from "../InfoCard";
import MentalWellbeingChart, {
  MentalWellbeingChartDataPoint,
} from "../MentalWellbeingChart";
import HereToHelpCard from "../../HereToHelpCard";
import MentalWellbeingModal from "../MentalWellbeingModal";
import { MetricItem } from "../../Common/types";

export interface MentalWellbeingScreenProps {
  depressionChartData: MentalWellbeingChartDataPoint[];
  anxietyChartData: MentalWellbeingChartDataPoint[];
  anxietyScore: number;
  depressionScore: number;
  lastUpdated?: string;
  onBack?: () => void;
  onRetakeAssessment?: () => void;
}

export default function MentalWellbeingScreen({
  depressionChartData,
  anxietyChartData,
  anxietyScore,
  depressionScore,
  lastUpdated,
  onBack,
  onRetakeAssessment,
}: MentalWellbeingScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const handleBack = onBack ?? (() => navigate(-1));

  return (
    <>
      <ScreenWrapper
        onBack={handleBack}
        title={t("MentalWellbeing.Screen.title")}
        description={t("MentalWellbeing.Screen.description")}
        onInfoClick={() => setIsInfoModalOpen(true)}
      >
        <Stack gap={2}>
          <MentalWellbeingInfoCard
            depressionScore={depressionScore}
            anxietyScore={anxietyScore}
            lastUpdated={lastUpdated}
            onCtaClick={onRetakeAssessment}
          />
          <MentalWellbeingChart
            depressionChartData={depressionChartData}
            anxietyChartData={anxietyChartData}
          />
          <HereToHelpCard
            title={t("MentalWellbeing.Screen.hereToHelp.title")}
            description={t("MentalWellbeing.Screen.hereToHelp.description")}
            button={{
              text: t("MentalWellbeing.Screen.hereToHelp.button"),
              icon: <IconLoader icon="SpeakToADoctorIcon" />,
            }}
          />
        </Stack>
      </ScreenWrapper>

      <MentalWellbeingModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </>
  );
}
